import { useSearchParams } from 'react-router-dom';
import { useCallback, useMemo } from 'react';

export type FilterModel = Record<string, { type: string; value: string }>;
export type SortModel = Record<string, 'asc' | 'desc'>;

export type AppSearchParams = {
  offset?: string;
  limit?: string;
  tab?: string;
  filters?: FilterModel;
  sort?: SortModel;
  direction?: 'asc' | 'desc';
};

export function useAppSearchParams() {
  const [searchParams, setSearchParams] = useSearchParams();

  // Parse the search parameters into a key-value object
  const allSearchParams = useMemo(
    () => Object.fromEntries(searchParams.entries()),
    [searchParams]
  );

  // Extract filters from searchParams
  const extractedFilters = useMemo(() => {
    return Object.entries(allSearchParams)
      .filter(([key]) => key.startsWith('q[') && key.endsWith(']'))
      .reduce((acc, [key, value]) => {
        const filterKey = key.slice(2, -1); // Remove "q[" and "]"
        const [type, filterValue] = value.split(':');

        // Set default type as 'eq' if none is provided
        acc[filterKey] = {
          type: filterValue ? type : 'eq',
          value: filterValue || value
        };

        return acc;
      }, {} as FilterModel);
  }, [allSearchParams]);

  // Helper to update search parameters
  // const updateSearchParams = useCallback(
  //   (params: Record<string, string | undefined>) => {
  //     const newParams = { ...allSearchParams };
  //
  //     Object.entries(params).forEach(([key, value]) => {
  //       if (value === undefined) {
  //         delete newParams[key];
  //       } else {
  //         newParams[key] = value;
  //       }
  //     });
  //
  //     setSearchParams(newParams);
  //   },
  //   [allSearchParams, setSearchParams]
  // );

  const updateSearchParams = useCallback(
    (params: Record<string, string | undefined>) => {
      setTimeout(() => {
        const newParams = { ...allSearchParams };

        Object.entries(params).forEach(([key, value]) => {
          if (value === undefined) {
            delete newParams[key];
          } else {
            newParams[key] = value;
          }
        });

        setSearchParams(newParams);
      }, 0);
    },
    [allSearchParams, setSearchParams]
  );

  const setParams = useCallback(
    (params: AppSearchParams) => {
      const { filters, sort, ...rest } = params;
      const newParams: Record<string, string> = {
        ...allSearchParams,
        ...rest
      };

      if (filters) {
        Object.entries(filters).forEach(([key, { type, value }]) => {
          newParams[`q[${key}]`] = `${type}:${value}`;
        });
      }

      if (sort) {
        Object.entries(sort).forEach(([key, direction]) => {
          newParams['sort'] = `${key}:${direction}`;
        });
      }

      updateSearchParams(newParams);
    },
    [allSearchParams, updateSearchParams]
  );

  const setFilters = useCallback(
    (filters?: FilterModel) => {
      // Create a new params object for the updates
      const newParams: Record<string, string | undefined> = {};

      // First, mark all existing filter params for removal
      // This ensures removed filters are actually removed from the URL
      Object.keys(allSearchParams).forEach(key => {
        if (key.startsWith('q[') && key.endsWith(']')) {
          newParams[key] = undefined;
        }
      });

      // Then add the new filter params
      if (filters && Object.keys(filters).length > 0) {
        Object.entries(filters).forEach(([key, { type, value }]) => {
          if (type && value) {
            newParams[`q[${key}]`] = `${type}:${value}`;
          }
        });
      }

      // Update the URL params
      updateSearchParams(newParams);
    },
    [allSearchParams, updateSearchParams]
  );

  const setSorting = useCallback(
    (sortField?: string, sortDirection?: 'asc' | 'desc') => {
      if (!sortField && !sortDirection) {
        updateSearchParams({
          sort: undefined,
          direction: undefined
        });
        return;
      }

      updateSearchParams({
        sort: sortField || undefined,
        direction: sortDirection || undefined
      });
    },
    [updateSearchParams]
  );

  const removeFilter = useCallback(
    (filterKey: string) => {
      updateSearchParams({ [`q[${filterKey}]`]: undefined });
    },
    [updateSearchParams]
  );

  const removeSearchParam = useCallback(
    (key: keyof AppSearchParams) => {
      updateSearchParams({ [key]: undefined });
    },
    [updateSearchParams]
  );

  const removeAllSearchParams = useCallback(() => {
    setSearchParams({});
  }, [setSearchParams]);

  return {
    searchParams: allSearchParams as AppSearchParams,
    filters: extractedFilters,
    sorting: allSearchParams.sort as string | undefined,
    direction: allSearchParams.direction as 'asc' | 'desc' | undefined,
    setSearchParams: setParams,
    setFilters,
    setSorting,
    removeFilter,
    removeSearchParam,
    removeAllSearchParams
  };
}
